// ============================================================
// ConsentEditCard.tsx — (Audit fix batch 2, 31 Oct — the new action-box
// design) the clean white edit card that opens BENEATH a consent notice once
// "Record consent" is pressed (the notice shrinks to one line "· recording…"):
//   header "Record consent" + who / what on the right; the fields on ONE row
//   (email · date · how agreed — passed in as children, each a FormField);
//   Save consent / Cancel at its foot and the note (e.g. "Kept on the client
//   record.") on the right. Focus moves to `focusId` when it opens.
// Used by the send windows (ConsentInline), Client details → Bills by email
// (BillEmailConsentCard) and the litigation friend's consent.
// ============================================================
import React, { useEffect, useId } from 'react'
import { B_SM_PRIMARY, LINK } from '../shared/ActionBox'
import { NOTE } from './billingUi'

interface Props {
  // Right of the header: "Ann Hughes · bills by email".
  context?: React.ReactNode
  // The field to focus on opening.
  focusId?: string
  busy?: boolean
  saveDisabled?: boolean
  saveLabel?: string
  onSave: () => void
  onCancel: () => void
  // Right of the foot: "Kept on the client record."
  footNote?: React.ReactNode
  // A save refusal that isn't about one field.
  error?: string | null
  children: React.ReactNode
}
const ConsentEditCard: React.FC<Props> = ({ context, focusId, busy, saveDisabled, saveLabel = 'Save consent', onSave, onCancel, footNote, error, children }) => {
  const id = useId()
  useEffect(() => { if (focusId) document.getElementById(focusId)?.focus() }, [focusId])
  return (
    <section role="group" aria-labelledby={`${id}-title`} className="rounded-[12px] border border-casevo-border bg-casevo-surface" data-consent-card="">
      <div className="flex items-baseline gap-3 px-4 pt-[12px] pb-[2px] flex-wrap">
        <h4 id={`${id}-title`} className="m-0 flex-1 text-[13.5px] font-semibold text-casevo-forest">Record consent</h4>
        {context && <span className="text-[12px] text-casevo-text-soft">{context}</span>}
      </div>
      <div className="grid gap-[12px] px-4 pt-[8px] pb-[12px] sm:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)] items-start">{children}</div>
      {error && <p role="alert" className="m-0 px-4 pb-[10px] text-[13px] text-casevo-danger">{error}</p>}
      <div className="flex items-center gap-2 flex-wrap px-4 py-[10px] border-t border-casevo-border-soft">
        <button type="button" className={B_SM_PRIMARY} disabled={busy || saveDisabled} onClick={onSave}>{busy ? 'Saving…' : saveLabel}</button>
        <button type="button" className={`${LINK} px-[8px]`} disabled={busy} onClick={onCancel}>Cancel</button>
        {footNote && <span className={`${NOTE} ml-auto`}>{footNote}</span>}
      </div>
    </section>
  )
}

export default ConsentEditCard
